import axios, { AxiosError, AxiosInstance, InternalAxiosRequestConfig } from 'axios';
import type { ApiError } from '@/types';
import { transformKeysToSnakeCase } from '@/utils/caseConversion';
import { useUpgradePromptStore } from '@/store/upgradePromptStore';
import { config, isProduction } from '@/config';
import { queryClient } from '@/queryClient';
import { coordinatedRefresh } from './refreshCoordinator';

// Validate HTTPS in production
if (isProduction && !config.apiUrl.startsWith('https://')) {
  throw new Error('API must use HTTPS in production');
}

/**
 * Error category for classifying API errors
 */
export type ErrorCategory =
  | 'auth'        // 401, 403
  | 'rate_limit'  // 429
  | 'validation'  // 400, 422
  | 'server'      // 500, 502, 503
  | 'network'     // No response (status 0)
  | 'unknown';

/**
 * WP-SEC-27: error code of the 403 the backend returns when an account whose
 * email is not verified asks for a session (password login, or any request
 * with a token issued before the rule). The UI offers to resend the link.
 */
export const EMAIL_NOT_VERIFIED_CODE = 'EMAIL_NOT_VERIFIED';

/**
 * Get the error category based on HTTP status code
 */
export function getErrorCategory(status: number): ErrorCategory {
  if (status === 401 || status === 403) return 'auth';
  if (status === 429) return 'rate_limit';
  if (status === 400 || status === 422) return 'validation';
  if (status >= 500) return 'server';
  if (status === 0) return 'network';
  return 'unknown';
}

/**
 * Custom error class for API errors
 */
export class ApiClientError extends Error {
  code: string;
  status: number;
  detail: string;
  field?: string;
  requestId: string;
  category: ErrorCategory;
  retryAfter?: number;

  constructor(error: ApiError, status: number, retryAfter?: number) {
    super(error.detail);
    this.name = 'ApiClientError';
    this.code = error.code;
    this.status = status;
    this.detail = error.detail;
    this.field = error.field;
    this.requestId = error.requestId;
    this.category = getErrorCategory(status);
    this.retryAfter = retryAfter;
  }
}

/**
 * Token management
 * WP-AUTH-12: httpOnly Cookie for Refresh Token Persistence
 *
 * Architecture:
 * - Access tokens: stored in memory only (cleared on page close)
 * - Refresh tokens: stored in httpOnly cookie by backend (not accessible from JS)
 * - User info (userId, name, email): localStorage for UX convenience
 *
 * The refresh token is automatically sent by the browser in the cookie
 * when making requests to /v1/auth/* endpoints (withCredentials: true).
 */
let accessToken: string | null = null;

/**
 * Set access token in memory.
 * Note: Refresh token is managed via httpOnly cookie by the backend.
 */
export const setAccessToken = (token: string | null): void => {
  accessToken = token;
};

/**
 * @deprecated Legacy compatibility - use setAccessToken() instead
 * Refresh token is now managed via httpOnly cookie, so the refresh param is ignored.
 */
export const setTokens = (access: string, _refresh?: string): void => {
  accessToken = access;
};

export const getAccessToken = (): string | null => {
  return accessToken;
};

/**
 * Clear access token from memory.
 * Note: Refresh token cookie is cleared by the backend on logout.
 */
export const clearTokens = (): void => {
  accessToken = null;
};

/**
 * @deprecated Use clearTokens() instead
 */
export const clearAccessToken = (): void => {
  clearTokens();
};

/**
 * Create axios instance with default configuration
 */
const createApiClient = (): AxiosInstance => {
  const client = axios.create({
    baseURL: config.apiUrl,
    timeout: 30000,
    headers: {
      'Content-Type': 'application/json',
    },
    // WP-AUTH-12: Enable cookies for httpOnly refresh token
    // This allows the browser to automatically send the refresh token cookie
    // on requests to /v1/auth/* endpoints
    withCredentials: true,
  });

  // Request interceptor - transform params and add auth token
  client.interceptors.request.use(
    (config: InternalAxiosRequestConfig) => {
      // Transform query parameter keys from camelCase to snake_case
      // Per API spec (docs/spec/api-surface.md Section 1.1):
      // - Response bodies use camelCase (handled by backend)
      // - Query parameters use snake_case (must be converted here)
      if (config.params) {
        config.params = transformKeysToSnakeCase(
          config.params as Record<string, unknown>
        );
      }

      // Add auth token
      const token = getAccessToken();
      if (token && config.headers) {
        config.headers.Authorization = `Bearer ${token}`;
      }
      return config;
    },
    (error: AxiosError) => {
      return Promise.reject(error);
    }
  );

  // Response interceptor - handle errors and token refresh
  client.interceptors.response.use(
    (response) => response,
    async (error: AxiosError<ApiError>) => {
      const originalRequest = error.config;

      // WP-SESSION-4: Handle 419 Session Idle Timeout
      // This MUST be before 401 handling - idle timeout is a distinct case
      if (error.response?.status === 419) {
        clearTokens();
        window.location.href = '/login?sessionExpired=idle';
        return Promise.reject(error);
      }

      // Handle 401 Unauthorized
      if (error.response?.status === 401 && originalRequest) {
        // Check if this is not already a retry and not the refresh endpoint
        const isRetry = (originalRequest as { _retry?: boolean })._retry;
        const isRefreshEndpoint = originalRequest.url?.includes('/auth/refresh');

        // Only attempt token refresh if user was previously authenticated.
        // This prevents redirect loops for login/register endpoints where 401
        // means "invalid credentials", not "session expired".
        const hadValidSession = !!accessToken;

        if (!isRetry && !isRefreshEndpoint && hadValidSession) {
          (originalRequest as { _retry?: boolean })._retry = true;

          try {
            // WP-AUTH-REFRESH-DEDUP-1 Layer 3: Pause queries during refresh
            // This prevents stale queries from triggering additional 401s
            queryClient.cancelQueries();

            // WP-AUTH-REFRESH-DEDUP-1: Use coordinated refresh for deduplication
            // This ensures only one refresh request is sent across all tabs
            const result = await coordinatedRefresh();
            const newAccessToken = result.accessToken;

            if (newAccessToken) {
              setAccessToken(newAccessToken);
              if (originalRequest.headers) {
                originalRequest.headers.Authorization = `Bearer ${newAccessToken}`;
              }
              return client(originalRequest);
            }
          } catch (refreshError) {
            // Refresh failed - clear tokens and redirect to login
            clearTokens();
            window.location.href = '/login';
            return Promise.reject(refreshError);
          }
        }

        // Only redirect if we had a valid session that's now invalid.
        // For unauthenticated requests (login, register), let the error propagate
        // so the UI can display the appropriate error message.
        if (hadValidSession) {
          clearTokens();
          window.location.href = '/login';
        }
      }

      // WP-SEC-27: a session whose account's email is not verified (a token
      // issued before the rule) is refused with 403 EMAIL_NOT_VERIFIED. Sign
      // out to the login page, which offers to resend the verification link.
      // A 403 on the login request itself (no session yet) is left to the page.
      const responseCode = (error.response?.data as unknown as { code?: unknown } | undefined)?.code;
      if (error.response?.status === 403 && responseCode === EMAIL_NOT_VERIFIED_CODE && accessToken) {
        clearTokens();
        window.location.href = '/login?emailNotVerified=1';
        return Promise.reject(error);
      }

      // WP-BILLING-4: Handle 402 Payment Required (entitlement blocked)
      // Show upgrade prompt instead of throwing error to allow graceful degradation
      if (error.response?.status === 402) {
        // Extract feature identifier from response
        // Cast through unknown for type safety
        const responseData = error.response.data as unknown as Record<string, unknown> | undefined;
        const feature = typeof responseData?.feature === 'string' ? responseData.feature : undefined;

        // Show upgrade prompt modal
        useUpgradePromptStore.getState().show(feature);

        // Return rejected promise with the error so calling code can handle it
        // (e.g., disable a button, show inline message)
        // The global modal handles the primary user interaction
      }

      // Transform error response to ApiClientError
      if (error.response?.data) {
        // Cast to unknown first to handle various error formats
        const rawData = error.response.data as unknown;
        // Handle different error formats:
        // - FastAPI HTTPException: {"detail": "..."} or {"detail": [{loc, msg, type}]}
        // - Our custom format: {code, detail, requestId}
        let apiError: ApiError;

        if (typeof rawData === 'object' && rawData !== null) {
          const data = rawData as Record<string, unknown>;
          // Extract detail - handle both string and array formats (Pydantic validation)
          let detail: string;
          if (typeof data.detail === 'string') {
            detail = data.detail;
          } else if (Array.isArray(data.detail)) {
            // Pydantic validation errors - extract messages
            detail = (data.detail as Array<{ msg?: string }>)
              .map((e) => e.msg || 'Validation error')
              .join(', ');
          } else if (typeof data.message === 'string') {
            // Some frameworks use 'message' instead of 'detail'
            detail = data.message;
          } else {
            detail = 'An error occurred';
          }

          apiError = {
            code: (typeof data.code === 'string' ? data.code : null) || `HTTP_${error.response.status}`,
            detail,
            field: typeof data.field === 'string' ? data.field : undefined,
            requestId: (typeof data.requestId === 'string' ? data.requestId : null) ||
              (typeof data.request_id === 'string' ? data.request_id : null) ||
              (error.response.headers?.['x-request-id'] as string) ||
              'unknown',
          };
        } else {
          apiError = {
            code: `HTTP_${error.response.status}`,
            detail: String(rawData) || 'An error occurred',
            requestId: 'unknown',
          };
        }

        // Extract Retry-After header for 429 rate limit errors
        let retryAfter: number | undefined;
        if (error.response.status === 429) {
          const retryAfterHeader = error.response.headers?.['retry-after'];
          if (retryAfterHeader) {
            const parsed = parseInt(retryAfterHeader, 10);
            if (!isNaN(parsed)) {
              retryAfter = parsed;
            }
          }
        }

        throw new ApiClientError(apiError, error.response.status, retryAfter);
      }

      // Network or other error
      throw new ApiClientError(
        {
          code: 'NETWORK_ERROR',
          detail: error.message || 'Network error occurred',
          requestId: 'unknown',
        },
        0
      );
    }
  );

  return client;
};

export const apiClient = createApiClient();

/**
 * WP-AUTH-REFRESH-DEDUP-1: Raw axios client for token refresh
 *
 * This client bypasses the 401 interceptor to prevent redirect loops.
 * ONLY use this for the /auth/refresh endpoint.
 *
 * Why this exists:
 * - The main apiClient has a 401 interceptor that tries to refresh tokens
 * - If the refresh call itself used apiClient, a refresh failure would trigger
 *   another refresh attempt, causing infinite loops
 * - This raw client makes the refresh call without any interceptors
 */
export const rawRefreshClient = axios.create({
  baseURL: config.apiUrl,
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
  // WP-AUTH-12: Enable cookies for httpOnly refresh token
  withCredentials: true,
});

/**
 * Request options for API methods
 */
export interface ApiRequestOptions {
  headers?: Record<string, string>;
  signal?: AbortSignal;
}

/**
 * Typed API methods
 */
export const api = {
  get: <T>(url: string, params?: Record<string, unknown>, options?: ApiRequestOptions) =>
    apiClient.get<T>(url, { params, headers: options?.headers, signal: options?.signal }).then((res) => res.data),

  post: <T>(url: string, data?: unknown, options?: ApiRequestOptions) =>
    apiClient.post<T>(url, data, { headers: options?.headers, signal: options?.signal }).then((res) => res.data),

  put: <T>(url: string, data?: unknown, options?: ApiRequestOptions) =>
    apiClient.put<T>(url, data, { headers: options?.headers, signal: options?.signal }).then((res) => res.data),

  patch: <T>(url: string, data?: unknown, options?: ApiRequestOptions) =>
    apiClient.patch<T>(url, data, { headers: options?.headers, signal: options?.signal }).then((res) => res.data),

  delete: <T>(url: string, data?: unknown, options?: ApiRequestOptions) =>
    apiClient.delete<T>(url, { data, headers: options?.headers, signal: options?.signal }).then((res) => res.data),
};

export default api;
